import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Bold, Italic, Underline, List, ListOrdered, Link2 } from 'lucide-react';

/**
 * Simple WYSIWYG for email body HTML.
 * Loads HTML visually (no raw tags); inserts {{ $placeholder }} tokens.
 */
export default function EmailBodyEditor({ value, onChange, placeholders = [], disabled = false }) {
  const ref = useRef(null);
  const skipNextValueSync = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    if (skipNextValueSync.current) {
      skipNextValueSync.current = false;
      return;
    }
    if ((ref.current.innerHTML || '') !== (value || '')) {
      ref.current.innerHTML = value || '';
    }
  }, [value]);

  const emit = () => {
    if (!ref.current) return;
    skipNextValueSync.current = true;
    onChange(ref.current.innerHTML || '');
  };

  const run = (command, arg = null) => {
    if (disabled) return;
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const insertLink = () => {
    const url = window.prompt('Link URL', 'https://');
    if (!url) return;
    run('createLink', url);
  };

  const insertPlaceholder = (name) => {
    if (disabled) return;
    ref.current?.focus();
    const token = `{{ $${name} }}`;
    // insertText keeps caret behavior; fallback to insertHTML
    const ok = document.execCommand('insertText', false, token);
    if (!ok) {
      document.execCommand('insertHTML', false, token);
    }
    emit();
  };

  return (
    <div className="rounded-lg border border-border bg-background/40 overflow-hidden">
      <div className="flex flex-wrap items-center gap-1 border-b border-border/60 px-2 py-1.5 bg-muted/20">
        <ToolbarBtn title="Bold" onClick={() => run('bold')} disabled={disabled}>
          <Bold className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Italic" onClick={() => run('italic')} disabled={disabled}>
          <Italic className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Underline" onClick={() => run('underline')} disabled={disabled}>
          <Underline className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <span className="w-px h-5 bg-border/80 mx-1" />
        <ToolbarBtn title="Bullet list" onClick={() => run('insertUnorderedList')} disabled={disabled}>
          <List className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Numbered list" onClick={() => run('insertOrderedList')} disabled={disabled}>
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarBtn>
        <ToolbarBtn title="Link" onClick={insertLink} disabled={disabled}>
          <Link2 className="h-3.5 w-3.5" />
        </ToolbarBtn>
      </div>

      {placeholders.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-2 py-2 border-b border-border/60 bg-muted/10">
          <span className="text-[11px] text-muted-foreground self-center mr-1">Insert:</span>
          {placeholders.map((name) => (
            <Button
              key={name}
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              className="h-7 text-[11px] px-2 border-border/70"
              onClick={() => insertPlaceholder(name)}
            >
              {`{{ $${name} }}`}
            </Button>
          ))}
        </div>
      )}

      <div
        ref={ref}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        className="min-h-[280px] max-h-[420px] overflow-y-auto px-3 py-3 text-sm leading-relaxed text-foreground outline-none focus:ring-0 [&_a]:text-primary [&_a]:underline"
        data-placeholder="Type email body here…"
        style={{ wordBreak: 'break-word' }}
      />
    </div>
  );
}

function ToolbarBtn({ children, onClick, title, disabled }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 disabled:opacity-40"
    >
      {children}
    </button>
  );
}
